import { send } from '@actual-app/core/platform/client/connection';
import { q } from '@actual-app/core/shared/query';
import { validForTransfer } from '@actual-app/core/shared/transfer';

import { aqlQuery } from '#queries/aqlQuery';

export type MatchTx = {
  id: string;
  account: string;
  amount: number;
  date: string; // YYYY-MM-DD
  transfer_id?: string | null;
  is_child?: boolean;
  payee?: string | null;
  cleared?: boolean;
};

export type TransferPair = {
  from: MatchTx; // money leaving (negative amount)
  to: MatchTx; // money arriving (positive amount)
};

// ~1 day gap across accounts is common, 3 leaves a little margin
export const DEFAULT_WINDOW_DAYS = 3;

function dayNumber(date: string): number {
  // YYYY-MM-DD parses as UTC midnight, so this is a stable integer day count
  return Math.round(Date.parse(date) / 86_400_000);
}

function byDateThenId(a: MatchTx, b: MatchTx): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  if (a.id !== b.id) return a.id < b.id ? -1 : 1;
  return 0;
}

// find opposite-amount transactions in different accounts whose dates fall within
// windowDays of each other. greedy 1:1 nearest-date pairing, each txn used once.
// when payeeIds is non-empty, both sides of a pair must have a payee in the set.
// when accountIds is non-empty, both sides must be in that account scope.
// when unambiguousOnly is set, a pair links only if each side's eligible
// counterpart set is exactly the other (no competing same-amount candidate).
// when onlyCleared is set, uncleared (pending) transactions are excluded | the
// unattended auto-linker uses this so freshly-imported bank pending, which are
// unstable and arrive in bursts, are never silently paired. Manual review still
// sees them.
export function matchTransfers(
  transactions: MatchTx[],
  opts?: {
    windowDays?: number;
    payeeIds?: string[];
    accountIds?: string[];
    unambiguousOnly?: boolean;
    onlyCleared?: boolean;
  },
): TransferPair[] {
  const windowDays = opts?.windowDays ?? DEFAULT_WINDOW_DAYS;
  const payeeSet =
    opts?.payeeIds && opts.payeeIds.length > 0 ? new Set(opts.payeeIds) : null;
  const accountSet =
    opts?.accountIds && opts.accountIds.length > 0
      ? new Set(opts.accountIds)
      : null;
  const unambiguousOnly = opts?.unambiguousOnly === true;
  const onlyCleared = opts?.onlyCleared === true;

  const eligible = transactions
    .filter(
      t =>
        t.transfer_id == null &&
        !t.is_child &&
        t.amount !== 0 &&
        (!onlyCleared || t.cleared !== false) &&
        (payeeSet === null || (t.payee != null && payeeSet.has(t.payee))) &&
        (accountSet === null || accountSet.has(t.account)),
    )
    .sort(byDateThenId);

  // amount -> txns with that exact amount (keeps the date-asc order above)
  const byAmount = new Map<number, MatchTx[]>();
  for (const t of eligible) {
    const list = byAmount.get(t.amount) ?? [];
    list.push(t);
    byAmount.set(t.amount, list);
  }

  // count eligible counterparts (opposite amount, different account, within
  // window) for a transaction | gates unambiguous-only linking
  function countCandidates(t: MatchTx): number {
    const cands = byAmount.get(-t.amount);
    if (!cands) return 0;
    let n = 0;
    for (const c of cands) {
      if (c.id === t.id || c.account === t.account) continue;
      if (Math.abs(dayNumber(t.date) - dayNumber(c.date)) <= windowDays) n += 1;
    }
    return n;
  }

  const used = new Set<string>();
  const pairs: TransferPair[] = [];

  for (const a of eligible) {
    if (used.has(a.id)) continue;
    const candidates = byAmount.get(-a.amount);
    if (!candidates) continue;

    let best: MatchTx | undefined;
    let bestDiff = Infinity;
    for (const b of candidates) {
      if (used.has(b.id) || b.id === a.id || b.account === a.account) continue;
      const diff = Math.abs(dayNumber(a.date) - dayNumber(b.date));
      if (diff > windowDays) continue;
      if (diff < bestDiff) {
        bestDiff = diff;
        best = b;
      }
    }
    if (!best) continue;

    // in unambiguous mode, link only when a and best are each other's sole
    // eligible counterpart | any competing same-amount candidate is left alone
    if (
      unambiguousOnly &&
      (countCandidates(a) !== 1 || countCandidates(best) !== 1)
    ) {
      continue;
    }

    used.add(a.id);
    used.add(best.id);
    const [from, to] = a.amount < 0 ? [a, best] : [best, a];
    pairs.push({ from, to });
  }

  return pairs;
}

export type TransferMatchItem = {
  from: MatchTx;
  to: MatchTx;
  fromAccountName: string;
  toAccountName: string;
};

export type TransferMatches = {
  matches: TransferMatchItem[];
};

// the backend calls (send + aqlQuery) are injectable so the gather/accept logic
// can be unit-tested without module-mocking the connection | mirrors how
// runTransferProfiles takes its deps.
export type TransferMatchDeps = {
  send: typeof send;
  aqlQuery: typeof aqlQuery;
};

export async function requestTransferMatches(
  opts?: {
    windowDays?: number;
    payeeIds?: string[];
    accountIds?: string[];
    unambiguousOnly?: boolean;
    onlyCleared?: boolean;
  },
  deps: TransferMatchDeps = { send, aqlQuery },
): Promise<TransferMatches> {
  const windowDays = opts?.windowDays ?? DEFAULT_WINDOW_DAYS;
  const payeeIds = opts?.payeeIds ?? [];
  const accountIds = opts?.accountIds ?? [];

  const accounts = (await deps.send('accounts-get')) as Array<{
    id: string;
    name: string;
  }>;
  const accountName = new Map(accounts.map(a => [a.id, a.name]));

  // skip split parents AND children | only plain (non-split) transactions can
  // be cross-account transfer candidates
  const baseFilter: Record<string, unknown> = {
    transfer_id: null,
    is_parent: false,
    is_child: false,
  };
  if (payeeIds.length > 0) {
    baseFilter.payee = { $oneof: payeeIds };
  }
  if (accountIds.length > 0) {
    baseFilter.account = { $oneof: accountIds };
  }

  const rows = (
    await deps.aqlQuery(
      q('transactions')
        .filter(baseFilter)
        .select([
          'id',
          'account',
          'amount',
          'date',
          'is_child',
          'payee',
          'cleared',
        ])
        .limit(20000),
    )
  ).data as MatchTx[];

  const matches = matchTransfers(rows, {
    windowDays,
    payeeIds,
    accountIds,
    unambiguousOnly: opts?.unambiguousOnly,
    onlyCleared: opts?.onlyCleared,
  }).map(p => ({
    from: p.from,
    to: p.to,
    fromAccountName: accountName.get(p.from.account) ?? p.from.account,
    toAccountName: accountName.get(p.to.account) ?? p.to.account,
  }));

  return { matches };
}

// link two existing transactions as a transfer without synthesizing a
// counterpart | mirrors onSetTransfer (useTransactionBatchActions): set each
// side's transfer-payee + transfer_id, clear the category, runTransfers:false.
export async function acceptTransferMatch(
  pair: {
    from: { id: string };
    to: { id: string };
  },
  deps: TransferMatchDeps = { send, aqlQuery },
): Promise<void> {
  const { data } = await deps.aqlQuery(
    q('transactions')
      .filter({ id: { $oneof: [pair.from.id, pair.to.id] } })
      .select('*'),
  );
  if (data.length !== 2) return;

  const [a, b] = data;
  if (!validForTransfer(a, b)) return;

  const payees = (await deps.send('payees-get')) as Array<{
    id: string;
    transfer_acct: string | null;
  }>;
  const aPayee = payees.find(p => p.transfer_acct === a.account);
  const bPayee = payees.find(p => p.transfer_acct === b.account);

  await deps.send('transactions-batch-update', {
    updated: [
      { ...a, category: null, payee: bPayee?.id, transfer_id: b.id },
      { ...b, category: null, payee: aPayee?.id, transfer_id: a.id },
    ],
    runTransfers: false,
  });
}
